import { lerpColor } from '../../color';
import { calmAt, calmCenter, glowDot, grain, rgba, vignette, type Painter } from '../../art/paint';
import { hsl, type World as Look } from '../../world';

const TAU = Math.PI * 2;

interface Tones {
  readonly bg: number;
  readonly far: number;
  readonly ceil: number;
  readonly ink: number;
  readonly rim: number;
  readonly L: number;
  readonly A1: number;
  readonly A2: number;
}

interface Frame {
  readonly portrait: boolean;
  readonly s: number;
  readonly trunk: number | null;
  readonly bulb: { readonly x: number; readonly y: number };
  readonly hook: number;
  readonly wall: number;
  readonly floor: number;
}

function tonesOf(look: Look): Tones {
  const p = look.palette;
  return { bg: p.bg, far: hsl(look.hue, 34, 16), ceil: look.ceil, ink: p.ink, rim: p.rim, L: p.incoming.circle, A1: p.held, A2: p.spot };
}

function frameOf(p: Painter): Frame {
  const s = p.unit / 320;
  if (p.w / p.h < 0.8) {
    const x = Math.max(p.w * 0.12, Math.min(p.w * 0.24, p.cx - p.unit * 0.85));
    const wall = Math.max(p.h * 0.7, p.cy + p.unit + 6 * s);
    return { portrait: true, s: s * 1.15, trunk: null, bulb: { x, y: p.h * 0.27 }, hook: p.h * 0.17, wall, floor: wall + p.h * 0.06 };
  }
  const trunk = Math.max(p.w * 0.1, p.cx - p.unit * 1.5);
  const x = Math.max(trunk + 50 * s, Math.min(trunk + p.unit * 0.4, p.cx - p.unit * 1.12));
  return { portrait: false, s, trunk, bulb: { x, y: p.h * 0.34 }, hook: p.h * 0.15, wall: p.h * 0.56, floor: p.h * 0.74 };
}

function bump(x: number, c: number, w: number): number {
  const d = (x - c) / w;
  return Math.exp(-d * d);
}

function edgeOf(p: Painter, f: Frame, x: number): number {
  const wave = Math.sin(x / (38 * f.s) + 1.3) * 8 * f.s + Math.sin(x / (17 * f.s)) * 4 * f.s;
  if (f.portrait) return p.h * (0.15 + 0.06 * bump(x, 0, p.w * 0.35) + 0.04 * bump(x, p.w, p.w * 0.3)) + wave;
  return p.h * (0.09 + 0.2 * bump(x, f.trunk ?? 0, p.w * 0.17) + 0.11 * bump(x, p.w, p.w * 0.17)) + wave;
}

function leafPath(ctx: CanvasRenderingContext2D, x: number, y: number, len: number, angle: number, wide: number): void {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const w = len * wide;
  const at = (u: number, v: number): [number, number] => [x + u * c - v * s, y + u * s + v * c];
  const [a1x, a1y] = at(len * 0.2, w);
  const [a2x, a2y] = at(len * 0.72, w * 0.75);
  const [tx, ty] = at(len, 0);
  const [b2x, b2y] = at(len * 0.72, -w * 0.75);
  const [b1x, b1y] = at(len * 0.2, -w);
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.bezierCurveTo(a1x, a1y, a2x, a2y, tx, ty);
  ctx.bezierCurveTo(b2x, b2y, b1x, b1y, x, y);
  ctx.closePath();
}

function night(p: Painter, t: Tones, f: Frame): void {
  const ctx = p.ctx;
  const g = ctx.createLinearGradient(0, 0, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.45)));
  g.addColorStop(0.45, rgba(t.bg));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.far, 0.4)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
  glowDot(ctx, f.bulb.x, f.bulb.y, p.unit * 1.1, t.L, 0.12);
}

function floor(p: Painter, t: Tones, f: Frame): void {
  const ctx = p.ctx;
  const g = ctx.createLinearGradient(0, f.floor, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.far, 0.45)));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.far, 0.2)));
  ctx.fillStyle = g;
  ctx.fillRect(0, f.floor, p.w, p.h - f.floor);
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.12), 0.35);
  ctx.lineWidth = 1.5 * f.s;
  ctx.beginPath();
  ctx.moveTo(0, f.floor);
  ctx.lineTo(p.w, f.floor);
  ctx.stroke();
  ctx.strokeStyle = rgba(lerpColor(t.bg, t.ink, 0.5), 0.35);
  ctx.lineWidth = 1;
  for (let i = 0; i < 7; i++) {
    let x = p.rng.float() * p.w;
    let y = f.floor + p.rng.range(0.15, 0.85) * (p.h - f.floor);
    if (calmAt(p, x, y, 0.95)) continue;
    ctx.beginPath();
    ctx.moveTo(x, y);
    for (let k = 0; k < 4; k++) {
      x += p.rng.range(-26, 26) * f.s;
      y += p.rng.range(-4, 8) * f.s;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  glowDot(ctx, f.bulb.x, f.floor + (p.h - f.floor) * 0.45, p.unit * 0.55, t.L, 0.07);
}

function walls(p: Painter, t: Tones, f: Frame): void {
  const ctx = p.ctx;
  const base = lerpColor(t.bg, t.far, 0.8);
  let x = -10;
  while (x < p.w + 10) {
    const span = p.rng.range(150, 280) * f.s;
    const top = f.wall + p.rng.range(-0.045, 0.03) * p.h;
    const tone = lerpColor(base, p.rng.chance(0.5) ? t.ceil : t.bg, p.rng.range(0.05, 0.22));
    ctx.fillStyle = rgba(tone);
    ctx.fillRect(x, top, span + 2, f.floor - top + 2);
    ctx.fillStyle = rgba(lerpColor(tone, t.rim, 0.1));
    ctx.fillRect(x - 2, top - 4 * f.s, span + 6, 5 * f.s);
    ctx.strokeStyle = rgba(lerpColor(tone, t.ink, 0.45), 0.22);
    ctx.lineWidth = 1;
    const row = 15 * f.s;
    for (let y = top + row; y < f.floor - 4; y += row) {
      let bx = x + p.rng.range(0, 20) * f.s;
      while (bx < x + span) {
        const len = p.rng.range(20, 60) * f.s;
        if (p.rng.chance(0.65)) {
          ctx.beginPath();
          ctx.moveTo(bx, y + p.rng.range(-1, 1));
          ctx.lineTo(Math.min(x + span, bx + len), y + p.rng.range(-1, 1));
          ctx.stroke();
        }
        bx += len + p.rng.range(8, 30) * f.s;
      }
    }
    if (p.rng.chance(0.6)) {
      ctx.fillStyle = rgba(lerpColor(tone, t.ceil, 0.35), 0.45);
      const pw = p.rng.range(30, 70) * f.s;
      const ph = p.rng.range(18, 34) * f.s;
      ctx.fillRect(x + p.rng.range(0.1, 0.7) * span, top + p.rng.range(10, 30) * f.s, pw, ph);
    }
    if (p.rng.chance(0.5)) {
      ctx.fillStyle = rgba(lerpColor(t.far, t.A2, 0.3), 0.5);
      for (let c = x + 6; c < x + span - 6; c += p.rng.range(7, 13) * f.s) {
        const hgt = p.rng.range(3, 7) * f.s;
        ctx.beginPath();
        ctx.moveTo(c - 2.5 * f.s, top - 3 * f.s);
        ctx.lineTo(c + p.rng.range(-1, 1) * f.s, top - 3 * f.s - hgt);
        ctx.lineTo(c + 2.5 * f.s, top - 3 * f.s);
        ctx.fill();
      }
    }
    x += span;
  }
}

function tank(p: Painter, t: Tones, x: number, y: number, s: number): void {
  const ctx = p.ctx;
  const w = 64 * s;
  const h = 46 * s;
  const body = lerpColor(t.far, t.A2, 0.16);
  ctx.fillStyle = rgba(lerpColor(t.far, t.ink, 0.3));
  ctx.fillRect(x - w * 0.42, y - 8 * s, w * 0.84, 8 * s);
  ctx.fillStyle = rgba(body);
  ctx.beginPath();
  ctx.moveTo(x - w * 0.5, y - h);
  ctx.lineTo(x + w * 0.5, y - h);
  ctx.lineTo(x + w * 0.42, y - 8 * s);
  ctx.lineTo(x - w * 0.42, y - 8 * s);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(body, t.rim, 0.08));
  ctx.beginPath();
  ctx.ellipse(x, y - h, w * 0.54, 7 * s, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(body, t.ink, 0.25));
  ctx.beginPath();
  ctx.ellipse(x, y - h - 4 * s, w * 0.12, 3.5 * s, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(body, t.L, 0.25), 0.5);
  ctx.lineWidth = 2 * s;
  ctx.beginPath();
  ctx.moveTo(x - w * 0.46, y - h + 4 * s);
  ctx.lineTo(x - w * 0.4, y - 10 * s);
  ctx.stroke();
}

function laundry(p: Painter, t: Tones, f: Frame, x0: number, y0: number, x1: number, y1: number): void {
  const ctx = p.ctx;
  const sagBy = (x1 - x0) * 0.08;
  const at = (k: number): { x: number; y: number } => ({ x: x0 + (x1 - x0) * k, y: y0 + (y1 - y0) * k + 4 * sagBy * k * (1 - k) });
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.25), 0.55);
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  for (let i = 0; i <= 20; i++) {
    const q = at(i / 20);
    if (i === 0) ctx.moveTo(q.x, q.y);
    else ctx.lineTo(q.x, q.y);
  }
  ctx.stroke();
  const tints = [t.A1, t.A2, t.L, t.rim];
  const n = Math.max(2, Math.round((x1 - x0) / (52 * f.s)));
  for (let i = 0; i < n; i++) {
    const k = (i + 0.5) / n;
    const q = at(k);
    if (calmAt(p, q.x, q.y + 20 * f.s, 0.95)) continue;
    const tone = lerpColor(t.far, tints[(i + p.level) % tints.length] ?? t.L, p.rng.range(0.22, 0.32));
    const s = f.s * p.rng.range(0.85, 1.1);
    ctx.fillStyle = rgba(tone, 0.92);
    const kind = (i * 7 + p.level) % 3;
    ctx.beginPath();
    if (kind === 0) {
      ctx.moveTo(q.x - 14 * s, q.y);
      ctx.lineTo(q.x + 14 * s, q.y);
      ctx.lineTo(q.x + 20 * s, q.y + 9 * s);
      ctx.lineTo(q.x + 13 * s, q.y + 12 * s);
      ctx.lineTo(q.x + 11 * s, q.y + 34 * s);
      ctx.lineTo(q.x - 11 * s, q.y + 34 * s);
      ctx.lineTo(q.x - 13 * s, q.y + 12 * s);
      ctx.lineTo(q.x - 20 * s, q.y + 9 * s);
    } else if (kind === 1) {
      ctx.moveTo(q.x - 11 * s, q.y);
      ctx.lineTo(q.x + 11 * s, q.y);
      ctx.lineTo(q.x + 12 * s, q.y + 24 * s);
      ctx.lineTo(q.x + 2 * s, q.y + 24 * s);
      ctx.lineTo(q.x, q.y + 12 * s);
      ctx.lineTo(q.x - 2 * s, q.y + 24 * s);
      ctx.lineTo(q.x - 12 * s, q.y + 24 * s);
    } else {
      ctx.moveTo(q.x - 10 * s, q.y);
      ctx.lineTo(q.x + 10 * s, q.y);
      ctx.lineTo(q.x + 9 * s, q.y + 30 * s);
      ctx.lineTo(q.x - 9 * s, q.y + 30 * s);
    }
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = rgba(lerpColor(t.far, t.rim, 0.4), 0.7);
    ctx.fillRect(q.x - 8 * s, q.y - 3 * s, 2.5 * s, 6 * s);
    ctx.fillRect(q.x + 6 * s, q.y - 3 * s, 2.5 * s, 6 * s);
  }
}

function trunk(p: Painter, t: Tones, f: Frame, x: number): void {
  const ctx = p.ctx;
  const s = f.s;
  const tone = lerpColor(t.bg, t.ink, 0.35);
  ctx.fillStyle = rgba(tone);
  ctx.beginPath();
  ctx.moveTo(x - 30 * s, p.h + 4);
  ctx.bezierCurveTo(x - 20 * s, p.h * 0.75, x - 22 * s, p.h * 0.5, x - 14 * s, p.h * 0.3);
  ctx.bezierCurveTo(x - 24 * s, p.h * 0.2, x - 50 * s, p.h * 0.12, x - 70 * s, f.hook * 0.4);
  ctx.lineTo(x - 52 * s, f.hook * 0.35);
  ctx.bezierCurveTo(x - 30 * s, p.h * 0.12, x - 6 * s, p.h * 0.2, x + 2 * s, p.h * 0.25);
  ctx.bezierCurveTo(x + 30 * s, p.h * 0.2, f.bulb.x - 10 * s, f.hook + 6 * s, f.bulb.x + 40 * s, f.hook - 4 * s);
  ctx.lineTo(f.bulb.x + 42 * s, f.hook + 7 * s);
  ctx.bezierCurveTo(f.bulb.x - 10 * s, f.hook + 18 * s, x + 30 * s, p.h * 0.26, x + 14 * s, p.h * 0.36);
  ctx.bezierCurveTo(x + 20 * s, p.h * 0.55, x + 18 * s, p.h * 0.8, x + 30 * s, p.h + 4);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(t.far, t.L, 0.3), 0.55);
  ctx.lineWidth = 3 * s;
  ctx.beginPath();
  ctx.moveTo(x + 14 * s, p.h * 0.38);
  ctx.bezierCurveTo(x + 20 * s, p.h * 0.55, x + 18 * s, p.h * 0.75, x + 24 * s, p.h * 0.9);
  ctx.stroke();
  ctx.strokeStyle = rgba(lerpColor(tone, t.ink, 0.5), 0.45);
  ctx.lineWidth = 1.4 * s;
  for (let i = 0; i < 6; i++) {
    const bx = x + p.rng.range(-14, 10) * s;
    const by = p.h * p.rng.range(0.42, 0.9);
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.quadraticCurveTo(bx + p.rng.range(-4, 4) * s, by + 20 * s, bx + p.rng.range(-3, 3) * s, by + p.rng.range(30, 55) * s);
    ctx.stroke();
  }
}

function branch(p: Painter, t: Tones, f: Frame): void {
  const ctx = p.ctx;
  const s = f.s;
  ctx.strokeStyle = rgba(lerpColor(t.far, t.bg, 0.25));
  ctx.lineCap = 'round';
  ctx.lineWidth = 9 * s;
  ctx.beginPath();
  ctx.moveTo(-10, f.hook - 30 * s);
  ctx.bezierCurveTo(p.w * 0.1, f.hook - 6 * s, f.bulb.x - 30 * s, f.hook + 6 * s, f.bulb.x + 46 * s, f.hook - 6 * s);
  ctx.stroke();
  ctx.lineCap = 'butt';
}

function canopy(p: Painter, t: Tones, f: Frame): void {
  const ctx = p.ctx;
  const mass = lerpColor(t.bg, t.ink, 0.5);
  ctx.fillStyle = rgba(mass);
  ctx.beginPath();
  ctx.moveTo(-10, -10);
  for (let x = -10; x <= p.w + 10; x += 6) ctx.lineTo(x, edgeOf(p, f, x) - 18 * f.s);
  ctx.lineTo(p.w + 10, -10);
  ctx.closePath();
  ctx.fill();
  const dark = lerpColor(lerpColor(t.bg, t.far, 0.55), t.A2, 0.06);
  const mid = lerpColor(lerpColor(t.bg, t.far, 0.95), t.A2, 0.09);
  const lit = lerpColor(lerpColor(t.far, t.ceil, 0.55), t.L, 0.12);
  const reach = p.unit * 1.15;
  const clusterAt = (x: number, y: number, n: number, len: number, depth: number): void => {
    for (let i = 0; i < n; i++) {
      const a = Math.PI / 2 + (i / Math.max(1, n - 1) - 0.5) * 2.2 + p.rng.range(-0.15, 0.15);
      const l = len * p.rng.range(0.75, 1.15);
      const tipX = x + Math.cos(a) * l;
      const tipY = y + Math.sin(a) * l;
      if (calmAt(p, tipX, tipY, 0.92)) continue;
      const light = Math.max(0, 1 - Math.hypot(tipX - f.bulb.x, tipY - f.bulb.y) / reach) * depth;
      const tone = lerpColor(depth < 0.5 ? dark : mid, lit, Math.min(1, light * 1.4));
      ctx.fillStyle = rgba(tone);
      leafPath(ctx, x, y, l, a, 0.24);
      ctx.fill();
      if (depth >= 1 && l > 30 * f.s) {
        ctx.strokeStyle = rgba(lerpColor(tone, t.ink, 0.45), 0.5);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + Math.cos(a) * l * 0.85, y + Math.sin(a) * l * 0.85);
        ctx.stroke();
      }
    }
  };
  const step = 40 * f.s;
  for (let x = p.rng.range(0, step); x < p.w + step; x += step * p.rng.range(0.7, 1.1)) {
    const y = edgeOf(p, f, x) - p.rng.range(40, 70) * f.s;
    clusterAt(x, y, 6, 40 * f.s, 0.4);
  }
  for (let x = p.rng.range(0, step); x < p.w + step; x += step * p.rng.range(0.85, 1.25)) {
    const y = edgeOf(p, f, x) - p.rng.range(12, 34) * f.s;
    clusterAt(x, y, 7, 46 * f.s, 1);
  }
}

function bulb(p: Painter, t: Tones, f: Frame): void {
  const ctx = p.ctx;
  const s = f.s;
  const { x, y } = f.bulb;
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.25), 0.8);
  ctx.lineWidth = 1.3 * s;
  ctx.beginPath();
  ctx.moveTo(x + 4 * s, f.hook);
  ctx.quadraticCurveTo(x + 6 * s, (f.hook + y) / 2, x, y - 10 * s);
  ctx.stroke();
  glowDot(ctx, x, y + 8 * s, 90 * s, t.L, 0.32);
  ctx.fillStyle = rgba(lerpColor(t.ink, t.far, 0.4));
  ctx.fillRect(x - 5 * s, y - 11 * s, 10 * s, 11 * s);
  ctx.fillStyle = rgba(lerpColor(t.L, t.rim, 0.4), 0.92);
  ctx.beginPath();
  ctx.ellipse(x, y + 9 * s, 8.5 * s, 11 * s, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(t.L, t.A1, 0.45), 0.7);
  ctx.lineWidth = 1.2 * s;
  ctx.beginPath();
  ctx.moveTo(x - 3 * s, y + 3 * s);
  ctx.lineTo(x - 1.5 * s, y + 9 * s);
  ctx.lineTo(x + 1.5 * s, y + 6 * s);
  ctx.lineTo(x + 3 * s, y + 3 * s);
  ctx.stroke();
}

function table(p: Painter, t: Tones, x: number, y: number, s: number): void {
  const ctx = p.ctx;
  const tone = lerpColor(t.far, t.rim, 0.17);
  ctx.fillStyle = rgba(lerpColor(tone, t.bg, 0.35));
  for (const lx of [-46, -30, 30, 46]) {
    ctx.beginPath();
    ctx.moveTo(x + lx * s - 3 * s, y + 4 * s);
    ctx.lineTo(x + lx * s * 1.08 - 2.5 * s, y + 52 * s);
    ctx.lineTo(x + lx * s * 1.08 + 2.5 * s, y + 52 * s);
    ctx.lineTo(x + lx * s + 3 * s, y + 4 * s);
    ctx.fill();
  }
  ctx.fillStyle = rgba(tone);
  ctx.beginPath();
  ctx.moveTo(x - 50 * s, y);
  ctx.lineTo(x + 50 * s, y);
  ctx.lineTo(x + 56 * s, y + 9 * s);
  ctx.lineTo(x - 56 * s, y + 9 * s);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(tone, t.L, 0.15));
  ctx.fillRect(x - 56 * s, y + 9 * s, 112 * s, 4 * s);
}

function chair(p: Painter, t: Tones, x: number, y: number, s: number, side: -1 | 1): void {
  const ctx = p.ctx;
  const tone = lerpColor(lerpColor(t.far, t.rim, 0.13), t.bg, 0.2);
  ctx.fillStyle = rgba(tone);
  ctx.beginPath();
  ctx.moveTo(x - 20 * s, y);
  ctx.lineTo(x + 20 * s, y);
  ctx.lineTo(x + 22 * s, y + 6 * s);
  ctx.lineTo(x - 22 * s, y + 6 * s);
  ctx.closePath();
  ctx.fill();
  for (const lx of [-19, 19]) ctx.fillRect(x + lx * s - 2 * s, y + 5 * s, 4 * s, 34 * s);
  const bx = x + side * 17 * s;
  ctx.beginPath();
  ctx.moveTo(bx - side * 4 * s, y);
  ctx.lineTo(bx + side * 2 * s, y - 40 * s);
  ctx.quadraticCurveTo(bx - side * 16 * s, y - 48 * s, bx - side * 34 * s, y - 40 * s);
  ctx.lineTo(bx - side * 30 * s, y - 32 * s);
  ctx.quadraticCurveTo(bx - side * 16 * s, y - 38 * s, bx - side * 4 * s, y - 32 * s);
  ctx.lineTo(bx - side * 8 * s, y);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(tone, t.bg, 0.5));
  for (let i = 0; i < 3; i++) ctx.fillRect(bx - side * (10 + i * 7) * s - 2 * s, y - 28 * s, 3.5 * s, 14 * s);
}

function surdo(p: Painter, t: Tones, x: number, y: number, s: number): void {
  const ctx = p.ctx;
  const w = 46 * s;
  const h = 58 * s;
  const shell = lerpColor(t.far, t.rim, 0.22);
  ctx.fillStyle = rgba(lerpColor(shell, t.bg, 0.3));
  ctx.fillRect(x - w, y - h, w * 2, h);
  ctx.beginPath();
  ctx.ellipse(x, y, w, 9 * s, 0, 0, Math.PI);
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(shell, t.ink, 0.4), 0.5);
  ctx.lineWidth = 1.5 * s;
  ctx.beginPath();
  for (let i = 0; i <= 6; i++) {
    const lx = x - w + (i / 6) * w * 2;
    ctx.moveTo(lx, y - h + 4 * s);
    ctx.lineTo(lx + (i % 2 === 0 ? 1 : -1) * 6 * s, y - 4 * s);
  }
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(t.far, t.L, 0.32));
  ctx.beginPath();
  ctx.ellipse(x, y - h, w, 10 * s, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(shell, t.rim, 0.15), 0.8);
  ctx.lineWidth = 2.5 * s;
  ctx.beginPath();
  ctx.ellipse(x, y - h, w, 10 * s, 0, 0, TAU);
  ctx.stroke();
}

function tanta(p: Painter, t: Tones, x: number, y: number, s: number): void {
  const ctx = p.ctx;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(-0.32);
  const shell = lerpColor(lerpColor(t.far, t.A1, 0.14), t.bg, 0.15);
  ctx.fillStyle = rgba(shell);
  ctx.fillRect(-17 * s, -62 * s, 34 * s, 62 * s);
  ctx.fillStyle = rgba(lerpColor(t.far, t.L, 0.3));
  ctx.beginPath();
  ctx.ellipse(0, -62 * s, 17 * s, 5 * s, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(t.far, t.rim, 0.3), 0.8);
  for (const ly of [-50, -12]) for (const lx of [-11, 0, 11]) ctx.fillRect(lx * s - 1.5 * s, ly * s, 3 * s, 7 * s);
  ctx.restore();
}

function cavaco(p: Painter, t: Tones, x: number, y: number, s: number, angle: number): void {
  const ctx = p.ctx;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  const wood = lerpColor(t.far, t.L, 0.24);
  ctx.fillStyle = rgba(lerpColor(wood, t.bg, 0.3));
  ctx.fillRect(-3 * s, -64 * s, 6 * s, 40 * s);
  ctx.fillRect(-5 * s, -76 * s, 10 * s, 13 * s);
  ctx.fillStyle = rgba(wood);
  ctx.beginPath();
  ctx.ellipse(0, -18 * s, 11 * s, 10 * s, 0, 0, TAU);
  ctx.ellipse(0, 0, 14 * s, 13 * s, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ink, 0.4));
  ctx.beginPath();
  ctx.arc(0, -9 * s, 4.5 * s, 0, TAU);
  ctx.fill();
  ctx.restore();
}

function pandeiro(p: Painter, t: Tones, x: number, y: number, s: number): void {
  const ctx = p.ctx;
  ctx.fillStyle = rgba(lerpColor(t.far, t.L, 0.28));
  ctx.beginPath();
  ctx.ellipse(x, y, 20 * s, 6 * s, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(t.far, t.A2, 0.3), 0.75);
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI + 0.3;
    ctx.beginPath();
    ctx.arc(x + Math.cos(a) * 20 * s, y + Math.sin(a) * 6 * s + 1.5 * s, 2.2 * s, 0, TAU);
    ctx.fill();
  }
}

function bottles(p: Painter, t: Tones, x: number, y: number, s: number): void {
  const ctx = p.ctx;
  const glass = lerpColor(t.far, t.L, 0.2);
  for (const dx of [-14, -2]) {
    ctx.fillStyle = rgba(glass, 0.85);
    ctx.fillRect(x + dx * s - 4 * s, y - 22 * s, 8 * s, 22 * s);
    ctx.fillRect(x + dx * s - 1.8 * s, y - 32 * s, 3.6 * s, 10 * s);
  }
  ctx.fillStyle = rgba(lerpColor(t.far, t.rim, 0.3), 0.6);
  ctx.fillRect(x + 10 * s, y - 13 * s, 8 * s, 13 * s);
  ctx.fillStyle = rgba(lerpColor(t.far, t.L, 0.45), 0.6);
  ctx.fillRect(x + 10.5 * s, y - 8 * s, 7 * s, 8 * s);
}

function roots(p: Painter, t: Tones, f: Frame, x: number): void {
  const ctx = p.ctx;
  ctx.strokeStyle = rgba(lerpColor(t.far, t.bg, 0.3));
  ctx.lineCap = 'round';
  for (let i = 0; i < 5; i++) {
    const dir = i % 2 === 0 ? 1 : -1;
    const len = p.rng.range(50, 110) * f.s;
    const y0 = p.h - p.rng.range(2, 30) * f.s;
    ctx.lineWidth = p.rng.range(5, 9) * f.s;
    ctx.beginPath();
    ctx.moveTo(x + dir * 14 * f.s, y0 - 10 * f.s);
    ctx.quadraticCurveTo(x + dir * len * 0.5, y0 + 4 * f.s, Math.max(4, Math.min(p.w - 4, x + dir * len)), y0 + p.rng.range(-4, 10) * f.s);
    ctx.stroke();
  }
  ctx.lineCap = 'butt';
}

function pot(p: Painter, t: Tones, x: number, y: number, s: number, sword: boolean): void {
  const ctx = p.ctx;
  const can = lerpColor(t.far, t.rim, 0.2);
  const green = lerpColor(lerpColor(t.far, t.A2, 0.26), t.bg, 0.1);
  ctx.fillStyle = rgba(green);
  if (sword) {
    for (let i = 0; i < 6; i++) {
      const lx = x + (i - 2.5) * 6 * s;
      const hgt = p.rng.range(52, 80) * s;
      ctx.beginPath();
      ctx.moveTo(lx - 4 * s, y - 30 * s);
      ctx.quadraticCurveTo(lx - 5 * s + (i - 2.5) * 2 * s, y - 30 * s - hgt * 0.6, lx + (i - 2.5) * 4 * s, y - 30 * s - hgt);
      ctx.quadraticCurveTo(lx + 5 * s + (i - 2.5) * 2 * s, y - 30 * s - hgt * 0.6, lx + 4 * s, y - 30 * s);
      ctx.fill();
    }
  } else {
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + (i - 2.5) * 0.45;
      leafPath(ctx, x, y - 30 * s, p.rng.range(36, 50) * s, a, 0.34);
      ctx.fill();
    }
  }
  ctx.fillStyle = rgba(can);
  ctx.fillRect(x - 17 * s, y - 32 * s, 34 * s, 32 * s);
  ctx.fillStyle = rgba(lerpColor(can, t.ink, 0.3), 0.6);
  ctx.fillRect(x - 17 * s, y - 32 * s, 34 * s, 3 * s);
  ctx.fillRect(x - 17 * s, y - 14 * s, 34 * s, 2 * s);
}

function roda(p: Painter, t: Tones, f: Frame): void {
  const full = p.level >= 3;
  const pots = p.level >= 4;
  if (f.portrait) {
    const s = f.s;
    const y = f.floor + (p.h * 0.9 - f.floor) * 0.35;
    const tx = p.w * 0.7;
    chair(p, t, tx - 70 * s, y + 10 * s, s, -1);
    table(p, t, tx, y, s);
    pandeiro(p, t, tx - 16 * s, y - 3 * s, s);
    cavaco(p, t, p.w * 0.85, y + 46 * s, s, 0.3);
    if (full) {
      bottles(p, t, tx + 20 * s, y, s);
      surdo(p, t, p.w * 0.12, y + 54 * s, s * 0.8);
      chair(p, t, p.w * 0.3, y + 14 * s, s, 1);
      tanta(p, t, p.w * 0.42, y + 54 * s, s * 0.85);
    }
    if (pots) {
      pot(p, t, p.w * 0.21, y + 62 * s, s * 0.8, true);
      pot(p, t, p.w * 0.93, y + 62 * s, s * 0.8, false);
    }
    return;
  }
  const s = f.s;
  const y = f.floor + 34 * s;
  const tx = p.w - Math.max(90 * s, p.w * 0.1);
  chair(p, t, tx - 78 * s, y + 18 * s, s, -1);
  table(p, t, tx, y, s);
  pandeiro(p, t, tx - 18 * s, y - 3 * s, s);
  cavaco(p, t, tx - 98 * s, y + 4 * s, s, -0.35);
  const lx = (f.trunk ?? p.w * 0.1) + 76 * s;
  if (full) {
    bottles(p, t, tx + 22 * s, y, s);
    chair(p, t, tx + 70 * s, y + 26 * s, s, 1);
    chair(p, t, lx + 40 * s, y + 22 * s, s, 1);
    surdo(p, t, lx - 10 * s, p.h - 14 * s, s);
    tanta(p, t, lx + 64 * s, p.h - 10 * s, s);
  }
  if (pots) {
    pot(p, t, (f.trunk ?? p.w * 0.1) - 75 * s, p.h - 8 * s, s, true);
    pot(p, t, tx - 30 * s, p.h - 6 * s, s * 0.85, false);
  }
}

export function paint(p: Painter, look: Look): void {
  const t = tonesOf(look);
  const f = frameOf(p);
  night(p, t, f);
  if (p.level >= 2) walls(p, t, f);
  floor(p, t, f);
  if (p.level >= 2) {
    if (f.portrait) {
      tank(p, t, p.w - 36 * f.s, f.wall - 0.02 * p.h, f.s * 0.8);
      laundry(p, t, f, p.w * 0.52, p.h * 0.215, p.w * 0.97, p.h * 0.205);
    } else {
      tank(p, t, p.w * 0.88, f.wall - 0.03 * p.h, f.s);
      laundry(p, t, f, p.w * 0.74, p.h * 0.4, p.w - 16 * f.s, p.h * 0.37);
    }
  }
  if (f.trunk !== null) {
    trunk(p, t, f, f.trunk);
    if (p.level >= 4) roots(p, t, f, f.trunk);
  } else branch(p, t, f);
  canopy(p, t, f);
  bulb(p, t, f);
  roda(p, t, f);
  calmCenter(p, t.bg, 0.62, 1.15);
  grain(p, 0.06, true);
  vignette(p, t.ink, 0.45);
}
